import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { MarketingNav, MarketingFooter } from '@/components/MarketingFooter';
import { FOUNDER } from '@/config/founder';
import { Users, Waves, ArrowRight, Building2, Linkedin, Instagram, CalendarCheck } from 'lucide-react';

/**
 * /about — Phase 92 (site audit fixes D3.3 + D5.3).
 *
 * Previously /about fell through the SPA catch-all and rendered the
 * homepage. This dedicated page carries the founder story, the SSU
 * community origin, a truthful development timeline, and the parent-
 * company disclosure — the trust content the audit flagged as missing.
 */
export default function About() {
    return (
        <div className="min-h-screen bg-white">
            <Seo
                title="About SplashPad — Built by Swim Coach Jacob Siegel"
                description="Meet Jacob Siegel: 12+ year swim instructor, founder of Superhero Swim Academy, SSU, and SplashPad — swim registration software backed by SSA MEDIA LLC."
                canonical="https://splashpad.pro/about"
                image={FOUNDER.photoUrl ? `https://splashpad.pro${FOUNDER.photoUrl}` : undefined}
                imageAlt={`${FOUNDER.name || 'Jacob Siegel'}, founder of SplashPad`}
                jsonLd={{
                    // Phase 126 (SEO audit) — page-level entity markup. References
                    // the site-wide @ids declared in /public/index.html so the
                    // founder/organization graph resolves cleanly.
                    '@context': 'https://schema.org',
                    '@type': 'AboutPage',
                    '@id': 'https://splashpad.pro/about#webpage',
                    url: 'https://splashpad.pro/about',
                    name: 'About SplashPad — Built by Swim Coach Jacob Siegel',
                    description: 'Meet Jacob Siegel: 12+ year swim instructor, founder of Superhero Swim Academy, SSU, and SplashPad — swim registration software backed by SSA MEDIA LLC.',
                    inLanguage: 'en-US',
                    isPartOf: { '@id': 'https://splashpad.pro/#website' },
                    about: { '@id': 'https://splashpad.pro/#organization' },
                    mainEntity: {
                        '@type': 'Person',
                        '@id': 'https://splashpad.pro/#founder',
                        name: FOUNDER.name || 'Jacob Siegel',
                        jobTitle: 'Founder',
                        description: FOUNDER.bio || 'Working swim instructor for 12+ years · Founder of Superhero Swim Academy & Superhero Swim University',
                        url: 'https://splashpad.pro/about',
                        ...(FOUNDER.photoUrl ? { image: `https://splashpad.pro${FOUNDER.photoUrl}` } : {}),
                        worksFor: { '@id': 'https://splashpad.pro/#organization' },
                        sameAs: [FOUNDER.instagramUrl, FOUNDER.linkedinUrl].filter(Boolean),
                    },
                }}
            />
            <MarketingNav />

            <section className="sd-gradient-hero">
                <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:py-20">
                    <Badge className="mb-4 border-[#ffd70066] bg-[#ffd70022] text-[#1a3a5c] hover:bg-[#ffd70033]">
                        <Waves className="mr-1 h-3 w-3" /> About SplashPad
                    </Badge>
                    <h1 className="font-display text-4xl font-bold leading-tight text-[#0a1628] sm:text-5xl">
                        Built on a pool deck, not in a boardroom.
                    </h1>
                    <p className="mt-5 max-w-2xl text-lg text-[#1a3a5c]">
                        SplashPad is swim school software designed by a working swim school owner
                        for small swim schools and coaches — from a solo instructor to a team with
                        staff across several pools — built and tested inside the Superhero Swim
                        University coaching community.
                    </p>
                </div>
            </section>

            {/* Founder block — renders once /config/founder.js is filled in. */}
            {FOUNDER.enabled && FOUNDER.name && (
                <section className="bg-white py-10">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6">
                        <Card className="sd-card flex flex-col items-center gap-5 p-6 sm:flex-row" data-testid="about-founder-card">
                            {FOUNDER.photoUrl && (
                                <img
                                    src={FOUNDER.photoUrl}
                                    alt={`${FOUNDER.name}, founder of SplashPad`}
                                    className="h-24 w-24 rounded-2xl object-cover"
                                />
                            )}
                            <div>
                                <div className="font-display text-xl font-bold text-[#0a1628]">{FOUNDER.name}</div>
                                <div className="text-sm text-[#1a3a5c]">{FOUNDER.bio}</div>
                                {FOUNDER.linkedinUrl && (
                                    <a
                                        href={FOUNDER.linkedinUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-sm text-[#00d4ff] hover:underline"
                                        data-testid="about-founder-linkedin"
                                    >
                                        <Linkedin className="h-4 w-4" /> Connect on LinkedIn
                                    </a>
                                )}
                                {FOUNDER.instagramUrl && (
                                    <a
                                        href={FOUNDER.instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-sm text-[#00d4ff] hover:underline"
                                        data-testid="about-founder-instagram"
                                    >
                                        <Instagram className="h-4 w-4" /> Follow @superheroswim
                                    </a>
                                )}
                            </div>
                        </Card>
                    </div>
                </section>
            )}

            {/* Why I built SplashPad — founder's voice. */}
            <section className="bg-white py-12">
                <div className="mx-auto max-w-3xl px-4 sm:px-6">
                    <h2 className="font-display text-3xl font-bold text-[#0a1628]">Why I built SplashPad</h2>
                    <div className="mt-5 space-y-4 text-[#1a3a5c]">
                        <p>
                            I&apos;m Jacob Siegel — most of my swimmers know me as Coach Jacob. I&apos;ve been
                            teaching swim lessons for over 12 years, and I run Superhero Swim Academy, my
                            own swim school. For years my &quot;business system&quot; was the same one most
                            independent coaches use: a spreadsheet for the roster, payment requests one
                            family at a time, paper waivers in a binder, and a group text for reminders.
                            Every hour in the water came with another hour of admin on the pool deck.
                        </p>
                        <p>
                            The turning point for my own school came when my business partner Reed Heller
                            and I moved everything online — card payments, registration, the whole flow.
                            It was a game changer. It saved us hours every week, made us look far more
                            professional to parents, and helped the business make real money because we
                            finally stopped chasing payments and paperwork.
                        </p>
                        <p>
                            Then, through Superhero Swim University — the coaching community I founded —
                            I met with over 300 swim instructors from all over the world. The same problem
                            kept coming up: most of them either had no registration software at all, or
                            were badly overpaying for one built for 500-student franchises with office
                            staff. A solo coach with 30 students doesn&apos;t need an enterprise suite. We
                            need one link we can text to a parent that handles registration, the waiver,
                            and the payment in one flow — and, once we hire our first coach, a staff
                            portal and payroll that don&apos;t cost franchise money.
                        </p>
                        <p>
                            So in 2025 I started building SplashPad with the coaches in the SSU community —
                            100+ working swim instructors who told me exactly what they needed, tested
                            every feature at their own pools, and pushed back when something didn&apos;t work
                            on a wet phone screen between lessons. I wanted every coach to experience the
                            same game changer I did — without spending an arm and a leg to get it. That&apos;s
                            why SplashPad charges a flat monthly price and just 25¢ per transaction — never a
                            percentage of what parents pay you — and why Pro comes with a 1-on-1 onboarding
                            call with someone who has actually taught a swim lesson.
                        </p>
                        <p className="font-semibold text-[#0a1628]">
                            — Jacob Siegel, founder of SplashPad · working swim instructor · founder of Superhero Swim Academy &amp; SSU
                        </p>
                    </div>
                </div>
            </section>

            {/* SSU origin */}
            <section className="bg-[#f4f7fb] py-12">
                <div className="mx-auto max-w-3xl px-4 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628]">
                        <Users className="mr-2 inline h-6 w-6 text-[#00d4ff]" />
                        Born inside the SSU community
                    </h2>
                    <p className="mt-4 text-[#1a3a5c]">
                        Superhero Swim University is a private Skool community of 100+ swim school owners
                        and instructors who swap lesson plans, pricing playbooks, and business advice.
                        SplashPad grew out of that group’s shared frustration with per-family fees and
                        legacy tools — and SSU members still get Starter free and Pro at half price.
                    </p>
                    <a
                        href="https://www.skool.com/superheroswimuniversity/about?ref=2eba9baa063e4d71916d29f262f0acd2"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#00d4ff] hover:underline"
                    >
                        Visit the SSU community on Skool <ArrowRight className="h-3.5 w-3.5" />
                    </a>
                </div>
            </section>

            {/* Development timeline — vendor-stability signal for cautious buyers. */}
            <section className="bg-white py-12">
                <div className="mx-auto max-w-3xl px-4 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628]">
                        <CalendarCheck className="mr-2 inline h-6 w-6 text-[#00d4ff]" />
                        Actively built, week after week
                    </h2>
                    <p className="mt-3 text-sm text-[#1a3a5c]/80">
                        SplashPad ships new features continuously — here’s the short version of the road so far.
                    </p>
                    <ul className="mt-6 space-y-4" data-testid="about-timeline">
                        {[
                            { when: '2025', what: 'SplashPad founded inside the SSU community. Branded registration pages, digital waivers, and automated Stripe payments ship to the first coaches.' },
                            { when: 'Late 2025', what: 'Class scheduling, attendance and rosters, parent portal, credits and refunds, broadcast email, and the public coach directory + world map.' },
                            { when: 'Early 2026', what: 'Multi-coach staff portal with shift scheduling, timeclock, and payroll exports. Installable mobile app (PWA). Two-factor authentication on every account.' },
                            { when: '2026', what: 'Open scheduling for parent self-booking, flexible lesson packs with per-date drop-ins, registration/assessment fees, savings calculator, and the guided setup wizard.' },
                        ].map((m) => (
                            <li key={m.when} className="flex gap-4">
                                <span className="mt-0.5 inline-flex h-8 shrink-0 items-center rounded-lg bg-[#0a1628] px-2.5 text-xs font-bold text-[#ffd700]">{m.when}</span>
                                <span className="text-sm text-[#1a3a5c]">{m.what}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Company facts */}
            <section className="bg-[#f4f7fb] py-12">
                <div className="mx-auto max-w-3xl px-4 sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628]">
                        <Building2 className="mr-2 inline h-6 w-6 text-[#00d4ff]" />
                        The company behind SplashPad
                    </h2>
                    <p className="mt-4 text-[#1a3a5c]">
                        SplashPad is owned and operated by <span className="font-semibold text-[#0a1628]">SSA MEDIA LLC</span>,
                        a US company. Payments are processed by Stripe — card details never touch our
                        servers. Questions? Email{' '}
                        <a href="mailto:support@splashpad.pro" className="text-[#00d4ff] hover:underline">support@splashpad.pro</a>{' '}
                        or read our <Link to="/security" className="text-[#00d4ff] hover:underline">security page</Link>.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-white py-14">
                <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
                    <h2 className="font-display text-2xl font-bold text-[#0a1628]">See it for yourself</h2>
                    <p className="mt-2 text-[#1a3a5c]">Free 30-day trial. Cancel anytime. Just 25¢ per transaction.</p>
                    <Link
                        to="/signup?role=instructor"
                        className="sd-cta sd-focus mt-5 inline-flex items-center rounded-xl px-6 py-3 text-base font-semibold"
                        data-testid="about-cta-trial"
                    >
                        Start My Free 30-Day Trial <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                </div>
            </section>

            <MarketingFooter />
        </div>
    );
}
